import { BacktestTab, DEFAULT_STRATEGY_FORM_DATA, StrategyFormData } from './types';

export const cloneBacktestingValue = <T>(data: T): T => JSON.parse(JSON.stringify(data)) as T;

export const buildStrategyDraft = (defaults?: Partial<StrategyFormData>): StrategyFormData => ({
  ...DEFAULT_STRATEGY_FORM_DATA,
  ...defaults,
  accountInformation: {
    ...DEFAULT_STRATEGY_FORM_DATA.accountInformation,
    ...defaults?.accountInformation,
  },
  execution: {
    ...DEFAULT_STRATEGY_FORM_DATA.execution,
    ...defaults?.execution,
  },
  hedging: {
    ...DEFAULT_STRATEGY_FORM_DATA.hedging,
    ...defaults?.hedging,
  },
  portfolio: {
    ...DEFAULT_STRATEGY_FORM_DATA.portfolio,
    ...defaults?.portfolio,
  },
  entryConditions: {
    ...DEFAULT_STRATEGY_FORM_DATA.entryConditions,
    ...defaults?.entryConditions,
  },
  exitConditions: {
    ...DEFAULT_STRATEGY_FORM_DATA.exitConditions,
    ...defaults?.exitConditions,
  },
  rollPositions: {
    ...DEFAULT_STRATEGY_FORM_DATA.rollPositions,
    ...defaults?.rollPositions,
  },
});

export const generateBacktestLabel = (existingTabs: BacktestTab[]): string => {
  const baseName = 'Backtest';
  const existingLabels = new Set(existingTabs.map((tab) => tab.label));

  if (!existingLabels.has(baseName)) return baseName;

  let counter = 2;
  while (existingLabels.has(`${baseName} ${counter}`)) {
    counter++;
  }
  return `${baseName} ${counter}`;
};

export const formatTradeStrategyLabel = (sequence: number) =>
  sequence <= 1 ? 'Trade Strategy' : `Trade Strategy ${sequence}`;

const COPY_SUFFIX_REGEX = /\s*\(Copy \d+\)$/;

export const generateCopyStrategyName = (sourceName: string, existingNames: string[]): string => {
  const baseName = sourceName.replace(COPY_SUFFIX_REGEX, '').trim();
  const usedIndexes = new Set(
    existingNames
      .map((name) => {
        const match = name.match(/\(Copy (\d+)\)$/);
        return match ? parseInt(match[1], 10) : null;
      })
      .filter((n): n is number => n !== null),
  );

  let index = 1;
  while (usedIndexes.has(index)) {
    index++;
  }
  return `${baseName} (Copy ${index})`;
};

export const getDuplicateStrategyIds = (
  strategies: Array<{ id: string; name: string }>,
): string[] => {
  const idsByName = strategies.reduce<Record<string, string[]>>((acc, strategy) => {
    const key = strategy.name.trim().toLowerCase();
    if (!key) return acc;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(strategy.id);
    return acc;
  }, {});

  return Object.values(idsByName)
    .filter((ids) => ids.length > 1)
    .flat();
};
